import { db, auth } from '../firebase-config.js';
import {
  doc, setDoc, getDoc, getDocs, deleteDoc, collection, query, where, limit, orderBy, runTransaction
} from 'firebase/firestore';
import { get } from 'svelte/store';
import { currentUser } from '../svelte/store.js';

// ─── OFFLINE SYNC QUEUE ─────────────────────────────────────────────────
// Stores failed ecosystem cloud writes and retries them when online.
const SYNC_QUEUE_KEY = 'cs_sync_queue';
const SYNC_QUEUE_MAX = 200;
const SYNC_MAX_ATTEMPTS = 8;      // give up rather than retry a broken payload forever
const SYNC_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function getSyncQueue() {
  try { return JSON.parse(localStorage.getItem(SYNC_QUEUE_KEY) || '[]'); } catch (e) { return []; }
}
function setSyncQueue(q) {
  const list = Array.isArray(q) ? q : [];
  // Trimming used to silently discard the OLDEST entries, which are exactly the
  // ones that have been waiting longest for a network. Warn loudly so a lost
  // offline match is at least visible in the crash log.
  if (list.length > SYNC_QUEUE_MAX) {
    console.warn(`[SyncQueue] Dropping ${list.length - SYNC_QUEUE_MAX} of the oldest queued cloud writes (cap ${SYNC_QUEUE_MAX}).`);
  }
  localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(list.slice(-SYNC_QUEUE_MAX)));
}
function enqueueSyncAction(type, payload, extra = {}) {
  const q = getSyncQueue();
  q.push({ type, payload, ...extra, _queuedAt: Date.now() });
  setSyncQueue(q);
}

let isProcessingQueue = false;
export async function processSyncQueue() {
  if (isProcessingQueue || !navigator.onLine) return;
  isProcessingQueue = true;
  const q = getSyncQueue();
  if (q.length === 0) { isProcessingQueue = false; return; }

  const remaining = [];
  const processedIds = new Set();
  let processed = 0;

  for (const item of q) {
    let success = false;
    try {
      if (item.type === 'pushMatch') {
        const user = get(currentUser);
        if (user?.uid) {
           const matchRef = doc(db, `users/${user.uid}/matches`, item.payload.id);
           await setDoc(matchRef, item.payload, { merge: true });
           success = true;
        } else { success = true; /* Drop if logged out */ }
      } else if (item.type === 'recordPerformance') {
        await setDoc(doc(db, 'performances', item.docId), item.payload);
        success = true;
      } else if (item.type === 'updateLastXI') {
        await setDoc(doc(db, 'teams', item.teamId), item.payload, { merge: true });
        success = true;
      } else {
        success = true; // unknown/legacy item type — drop it
      }
    } catch (error) {
      // A create-only performance doc that already exists throws
      // permission-denied — that is the intended dedup, so drop it instead of
      // retrying forever.
      if (error?.code === 'permission-denied') success = true;
      else console.warn(`[SyncQueue] Retry failed for ${item.type}:`, error);
    }
    if (success) { 
      processed++; 
      processedIds.add(item.id || JSON.stringify(item));
      continue; 
    }

    // Bound the retries. Without this, a permanently-broken payload was
    // re-attempted on every 'online' event forever, burning battery and network
    // for the life of the install.
    item._attempts = (Number(item._attempts) || 0) + 1;
    const tooOld = item._queuedAt && (Date.now() - item._queuedAt) > SYNC_MAX_AGE_MS;
    if (item._attempts >= SYNC_MAX_ATTEMPTS || tooOld) {
      console.error(`[SyncQueue] Giving up on ${item.type} after ${item._attempts} attempts${tooOld ? ' (expired)' : ''}.`);
      processedIds.add(item.id || JSON.stringify(item));
      continue;
    }
    remaining.push(item);
  }

  const originalIds = new Set(q.map(r => r.id || JSON.stringify(r)));
  const latestQueue = getSyncQueue();
  const newlyAdded = latestQueue.filter(item => {
    const key = item.id || JSON.stringify(item);
    return !originalIds.has(key);
  });
  setSyncQueue([...remaining, ...newlyAdded]);
  isProcessingQueue = false;
  if (processed > 0) {
    console.log(`[SyncQueue] Successfully processed ${processed} pending items.`);
  }
}

let _syncQueueInited = false;
export function initSyncQueue() {
  if (_syncQueueInited) return; // idempotent — avoid double 'online' listeners
  _syncQueueInited = true;
  window.addEventListener('online', () => setTimeout(processSyncQueue, 3000));
}

// ─── PII protection ─────────────────────────────────────────────────────
// The public player directory lives in `profiles/{uid}` and contains ONLY
// non-sensitive fields (username, display name, avatar, stats). Email lookup
// works through a salted SHA-256 hash so raw emails are never enumerable.
// Raw email/phone stay in the private `users/{uid}` doc (owner-only rules).
const LOOKUP_SALT = 'ffpl-cricket-scorer-v1';

async function sha256Hex(text) {
  const bytes = new TextEncoder().encode(LOOKUP_SALT + '|' + String(text).trim().toLowerCase());
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes));
    return Array.from(digest, b => b.toString(16).padStart(2, '0')).join('');
  } else {
    // Fallback if crypto.subtle is unavailable (e.g. non-HTTPS)
    let hash = 0;
    for (let i = 0; i < bytes.length; i++) {
      hash = ((hash << 5) - hash) + bytes[i];
      hash |= 0;
    }
    return Math.abs(hash).toString(16).padStart(16, '0');
  }
}

/**
 * Pushes a match object to Firestore for the currently logged-in user.
 * @param {Object} match - The match object from activeMatch or savedMatches
 * @returns {Promise<boolean>} - True if successful, false otherwise
 */
export async function pushMatchToCloud(match) {
  const user = get(currentUser);
  if (!user || !user.uid) {
    return false; // Not logged in (or guest), can't sync
  }
  if (!match || !match.id) {
    return false; // Invalid match data
  }

  try {
    // We only save the necessary JSON data. No video blobs.
    // Ensure we don't accidentally push proxy objects or functions
    const payload = JSON.parse(JSON.stringify(match));

    // Add a cloud timestamp
    payload._lastCloudSync = new Date().toISOString();

    const matchRef = doc(db, `users/${user.uid}/matches`, match.id);
    const snap = await getDoc(matchRef);
    if (snap.exists() && (snap.data()?._stateRevision || 0) > (match._stateRevision || 0)) {
      console.warn(`[CloudSync] Cloud is newer (${snap.data()._stateRevision} > ${match._stateRevision}), aborting local overwrite.`);
      return false;
    }
    // Offline-safe: the write is staged in the persistent cache immediately;
    // wait briefly for the server ack, but never hang the caller offline —
    // the backup auto-syncs when the network returns.
    const ack = setDoc(matchRef, payload, { merge: true }).then(() => true);
    const result = await Promise.race([
      ack.catch(error => { console.error(`[CloudSync] Failed to sync match ${match?.id}:`, error); return false; }),
      new Promise(resolve => setTimeout(() => resolve('staged'), 5000))
    ]);
    if (result === 'staged') {
      console.log(`[CloudSync] Offline — match ${match.id} backup queued, will auto-sync.`);
      return true;
    }
    return result;
  } catch (error) {
    console.error(`[CloudSync] Failed to sync match ${match?.id}:`, error);
    return false;
  }
}

/**
 * Deletes a match from the cloud storage for the currently logged-in user.
 * @param {string} matchId - The ID of the match to delete
 */
export async function deleteMatchFromCloud(matchId) {
  const user = get(currentUser);
  if (!user || !user.uid) {
    return false; // Not logged in
  }
  if (!matchId) {
    return false;
  }

  try {
    const matchRef = doc(db, `users/${user.uid}/matches`, matchId);
    await deleteDoc(matchRef);
    return true;
  } catch (error) {
    console.error("Error deleting match from cloud:", error);
    return false;
  }
}

/**
 * Fetches all matches for the currently logged-in user from Firestore.
 * @returns {Promise<Array>} - Array of match objects
 */
export async function fetchMatchesFromCloud() {
  const user = get(currentUser);
  if (!user || !user.uid) {
    return [];
  }

  try {
    const matchesRef = collection(db, `users/${user.uid}/matches`);
    const snapshot = await getDocs(query(matchesRef, limit(50)));

    const matches = [];
    snapshot.forEach((docSnap) => {
      matches.push(docSnap.data());
    });
    return matches;
  } catch (error) {
    console.error(`[CloudSync] Failed to fetch matches:`, error);
    return [];
  }
}

/**
 * Fetches the user's profile from Firestore, creating one if it doesn't exist.
 * Plan and admin status are SERVER-MANAGED fields: they are only ever read
 * here. The single client-side transition allowed by the security rules is
 * the automatic downgrade to 'free' when a subscription expires.
 */
export async function fetchUserProfile() {
  const user = get(currentUser);
  if (!user || !user.uid) return null;

  try {
    const profileRef = doc(db, 'users', user.uid);
    const snap = await getDoc(profileRef);
    if (snap.exists()) {
      let profile = snap.data();

      // AUTO-EXPIRY CHECK — downgrade an expired subscription.
      if (profile.plan === 'pro' && profile.planExpiry) {
        let serverTimeMs = Date.now();
        try {
          const res = await fetch(window.location.origin, { method: 'HEAD', cache: 'no-cache' });
          if (res.headers.has('date')) {
            serverTimeMs = new Date(res.headers.get('date')).getTime();
          }
        } catch (_) {}
        if (serverTimeMs > profile.planExpiry) {
          profile.plan = 'free';
          await setDoc(profileRef, { plan: 'free', planExpiry: null }, { merge: true });
        }
      }
      return profile;
    } else {
      // Create default profile
      const defaultProfile = { plan: 'free', createdAt: new Date().toISOString() };
      await setDoc(profileRef, defaultProfile);
      return defaultProfile;
    }
  } catch (error) {
    console.error(`[CloudSync] Failed to fetch user profile:`, error);
    return null;
  }
}

/**
 * Checks if a username is already taken.
 * Fail-CLOSED: if the check cannot run (offline / error) the username is
 * reported unavailable, because usernames are permanent once claimed.
 * @returns {Promise<{available: boolean, offline?: boolean}>}
 */
export async function checkUsernameAvailability(username, currentUid) {
  const clean = String(username || '').trim().toLowerCase();
  if (!clean) return { available: false };
  try {
    const snap = await getDoc(doc(db, 'usernames', clean));
    if (!snap.exists()) return { available: true };
    return { available: snap.data()?.uid === currentUid };
  } catch (error) {
    console.error(`[CloudSync] Username check failed:`, error);
    return { available: false, offline: true };
  }
}

/**
 * Atomically claims a username for the given uid. Two devices racing for the
 * same handle can never both win — the Firestore transaction re-reads the
 * reservation doc at commit time.
 * @returns {Promise<{ok: boolean, reason?: 'taken'|'offline'}>}
 */
export async function claimUsername(username, uid) {
  const clean = String(username || '').trim().toLowerCase();
  if (!clean || !uid) return { ok: false, reason: 'taken' };
  try {
    await runTransaction(db, async (tx) => {
      const ref = doc(db, 'usernames', clean);
      const snap = await tx.get(ref);
      if (snap.exists() && snap.data()?.uid !== uid) {
        throw new Error('USERNAME_TAKEN');
      }
      tx.set(ref, { uid, claimedAt: new Date().toISOString() });
    });
    return { ok: true };
  } catch (error) {
    if (error && error.message === 'USERNAME_TAKEN') return { ok: false, reason: 'taken' };
    console.error(`[CloudSync] Username claim failed:`, error);
    return { ok: false, reason: 'offline' };
  }
}

/**
 * Pushes the user's profile to Firestore.
 * Private doc (`users/{uid}`) keeps everything including email/phone.
 * Public doc (`profiles/{uid}`) gets only the searchable, non-sensitive
 * fields plus a salted email hash for lookups.
 */
export async function pushUserProfileToCloud(profileData) {
  const user = get(currentUser);
  if (!user || !user.uid) return false;

  try {
    const publicProfile = {
      username: profileData.username || '',
      displayName: profileData.displayName || '',
      photo: profileData.photo || '',
      role: profileData.role || '',
      battingStyle: profileData.battingStyle || '',
      bowlingType: profileData.bowlingType || '',
      age: profileData.age || '',
      stats: profileData.stats || {},
      emailHash: profileData.email ? await sha256Hex(profileData.email) : '',
      updatedAt: profileData.updatedAt || new Date().toISOString()
    };

    // Offline-safe: stage both writes together, bounded wait (they auto-sync
    // via the persistent cache when the network returns).
    const acks = Promise.all([
      setDoc(doc(db, 'users', user.uid), profileData, { merge: true }),
      setDoc(doc(db, 'profiles', user.uid), publicProfile, { merge: true })
    ]).then(() => true);
    return await Promise.race([
      acks.catch(error => { console.error(`[CloudSync] Failed to push user profile:`, error); return false; }),
      new Promise(resolve => setTimeout(() => resolve(true), 5000))
    ]);
  } catch (error) {
    console.error(`[CloudSync] Failed to push user profile:`, error);
    return false;
  }
}

/**
 * Searches the public player directory by username or email.
 * (Phone-number search was removed: numbers are user-entered and unverified,
 * so exposing them for lookup enabled both PII leaks and impersonation.)
 */
export async function searchPlayerInCloud(searchTerm) {
  const cleanTerm = String(searchTerm).trim();
  if (!cleanTerm) return null;

  try {
    const profilesRef = collection(db, 'profiles');

    // 1. Search by username
    const qUsername = query(profilesRef, where('username', '==', cleanTerm.toLowerCase()));
    const snapshot = await getDocs(qUsername);
    if (!snapshot.empty) {
      const docVal = snapshot.docs[0];
      return { uid: docVal.id, ...docVal.data() };
    }

    // 2. Search by salted email hash (raw emails are never stored publicly)
    if (cleanTerm.includes('@')) {
      const emailHash = await sha256Hex(cleanTerm);
      const qEmail = query(profilesRef, where('emailHash', '==', emailHash));
      const snapEmail = await getDocs(qEmail);
      if (!snapEmail.empty) {
        const docVal = snapEmail.docs[0];
        return { uid: docVal.id, ...docVal.data() };
      }
    }
  } catch (error) {
    console.error(`[CloudSync] Search player failed:`, error);
  }
  return null;
}

/**
 * Permanently deletes the signed-in user's cloud data and Firebase account.
 * Order matters: Firestore docs first (they need an authenticated user),
 * auth account last. Throws 'auth/requires-recent-login' passthrough so the
 * caller can re-authenticate and retry.
 */
export async function deleteUserData(uid) {
  // 1. Cloud match backups
  try {
    const snapshot = await getDocs(collection(db, `users/${uid}/matches`));
    for (const d of snapshot.docs) {
      await deleteDoc(d.ref);
    }
  } catch (error) {
    console.warn('[CloudSync] Match backup cleanup failed (continuing):', error);
  }

  // 2. Username reservation
  try {
    const privateSnap = await getDoc(doc(db, 'users', uid));
    const username = privateSnap.exists() ? String(privateSnap.data()?.username || '').toLowerCase() : '';
    if (username) await deleteDoc(doc(db, 'usernames', username));
  } catch (error) {
    console.warn('[CloudSync] Username cleanup failed (continuing):', error);
  }

  // 3. Public + private profile docs
  try { await deleteDoc(doc(db, 'profiles', uid)); } catch (_) {}
  await deleteDoc(doc(db, 'users', uid));
}

export async function deleteAccountAndData() {
  const user = get(currentUser);
  const fbUser = auth.currentUser;
  if (!user || !user.uid || !fbUser) throw new Error('Not signed in.');
  const uid = user.uid;

  // Delete data first (can be retried), then auth (irreversible)
  await deleteUserData(uid);
  await fbUser.delete(); // Firebase Auth
}

// ─── GLOBAL TEAMS & ECOSYSTEM ───────────────────────────────────────────
// Roster entries are denormalized: { uid, name, username, photo }. Guests
// (players without an app account) have uid: null. `rosterUids` mirrors the
// registered uids for array-contains queries and the lastXI security rule.

function _rosterUids(roster) {
  return roster.filter(r => r.uid).map(r => r.uid);
}

const TEAM_HANDLE_RE = /^[a-z0-9_]{3,20}$/;

/**
 * Creates a new Global Team, atomically claiming its unique @handle.
 * The creator becomes captain and the first roster member.
 * @returns {Promise<Object>} the created team doc
 */
export async function createGlobalTeam(teamName, teamUsername) {
  const user = get(currentUser);
  if (!user || !user.uid) throw new Error('Not signed in.');
  const cleanName = String(teamName || '').trim().slice(0, 30);
  const cleanUsername = String(teamUsername || '').trim().toLowerCase().replace(/^@/, '');
  if (!cleanName) throw new Error('Enter a team name.');
  if (!TEAM_HANDLE_RE.test(cleanUsername)) {
    throw new Error('Team handle: 3-20 chars, only a-z, 0-9 and _');
  }

  // Denormalize the captain's public profile into the roster entry.
  let myProfile = null;
  try {
    const snap = await getDoc(doc(db, 'profiles', user.uid));
    if (snap.exists()) myProfile = snap.data();
  } catch (_) {}

  const teamId = doc(collection(db, 'teams')).id;
  const teamData = {
    id: teamId,
    name: cleanName,
    username: cleanUsername,
    captainUid: user.uid,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    roster: [{
      uid: user.uid,
      name: myProfile?.displayName || user.displayName || 'Captain',
      username: myProfile?.username || '',
      photo: myProfile?.photo || ''
    }],
    rosterUids: [user.uid],
    lastXI: []
  };

  // Atomic handle claim + team creation in one transaction — two captains
  // racing for the same handle can never both win.
  await runTransaction(db, async (tx) => {
    const handleRef = doc(db, 'teamUsernames', cleanUsername);
    const handleSnap = await tx.get(handleRef);
    if (handleSnap.exists()) throw new Error('TEAM_HANDLE_TAKEN');
    tx.set(handleRef, { teamId, uid: user.uid, claimedAt: new Date().toISOString() });
    tx.set(doc(db, 'teams', teamId), teamData);
  }).catch(err => {
    if (err && err.message === 'TEAM_HANDLE_TAKEN') {
      throw new Error(`@${cleanUsername} is already taken — try another handle.`);
    }
    throw err;
  });
  return teamData;
}

/**
 * Searches for a global team by its exact @handle.
 */
export async function searchGlobalTeam(teamUsername) {
  const cleanTerm = String(teamUsername || '').trim().toLowerCase().replace(/^@/, '');
  if (!cleanTerm) return null;

  try {
    const qUsername = query(collection(db, 'teams'), where('username', '==', cleanTerm));
    const snapshot = await getDocs(qUsername);
    if (!snapshot.empty) {
      const docVal = snapshot.docs[0];
      return { id: docVal.id, ...docVal.data() };
    }
  } catch (error) {
    console.error(`[CloudSync] Search team failed:`, error);
  }
  return null;
}

/**
 * Adds a player to the roster (captain only).
 * @param {Object} player - { uid, name, username, photo } — uid null for guests.
 */
export async function addPlayerToTeam(teamId, player) {
  const user = get(currentUser);
  if (!user || !user.uid) throw new Error('Not signed in.');
  const entry = {
    uid: player?.uid || null,
    name: String(player?.name || player?.displayName || '').trim().slice(0, 30),
    username: player?.username || '',
    photo: player?.photo || ''
  };
  if (!entry.name) throw new Error('Player name required.');

  const teamRef = doc(db, 'teams', teamId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(teamRef);
    if (!snap.exists()) throw new Error('Team not found');
    const data = snap.data();
    if (data.captainUid !== user.uid) throw new Error('Only the captain can add players.');

    const roster = data.roster || [];
    const dup = roster.find(r => (entry.uid && r.uid === entry.uid) ||
      r.name.toLowerCase() === entry.name.toLowerCase());
    if (dup) throw new Error(`${entry.name} is already in the roster.`);
    if (roster.length >= 25) throw new Error('Roster is full (25 players max).');

    roster.push(entry);
    tx.update(teamRef, { roster, rosterUids: _rosterUids(roster), updatedAt: new Date().toISOString() });
  });
  return true;
}

/**
 * Removes a roster member (captain only; the captain cannot remove themself).
 */
export async function removePlayerFromTeam(teamId, playerName) {
  const user = get(currentUser);
  if (!user || !user.uid) throw new Error('Not signed in.');

  const teamRef = doc(db, 'teams', teamId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(teamRef);
    if (!snap.exists()) throw new Error('Team not found');
    const data = snap.data();
    if (data.captainUid !== user.uid) throw new Error('Only the captain can remove players.');

    const roster = (data.roster || []).filter(r => {
      if (r.name !== playerName) return true;
      if (r.uid === data.captainUid) throw new Error('Captain cannot be removed.');
      return false;
    });
    tx.update(teamRef, { roster, rosterUids: _rosterUids(roster), updatedAt: new Date().toISOString() });
  });
  return true;
}

/**
 * Deletes a team and frees its @handle (captain only).
 */
export async function deleteGlobalTeam(team) {
  const user = get(currentUser);
  if (!user || !user.uid) throw new Error('Not signed in.');
  if (team.captainUid !== user.uid) throw new Error('Only the captain can delete the team.');
  await deleteDoc(doc(db, 'teams', team.id));
  try { await deleteDoc(doc(db, 'teamUsernames', team.username)); } catch (_) {}
  return true;
}

/**
 * Fetches teams where the current user is captain OR a roster member.
 */
export async function fetchMyGlobalTeams() {
  const user = get(currentUser);
  if (!user || !user.uid) return [];

  try {
    const teamsRef = collection(db, 'teams');
    const [asCaptain, asPlayer] = await Promise.all([
      getDocs(query(teamsRef, where('captainUid', '==', user.uid))),
      getDocs(query(teamsRef, where('rosterUids', 'array-contains', user.uid)))
    ]);
    const seen = new Set();
    const teams = [];
    for (const snap of [asCaptain, asPlayer]) {
      snap.forEach(docSnap => {
        if (seen.has(docSnap.id)) return;
        seen.add(docSnap.id);
        teams.push({ id: docSnap.id, ...docSnap.data() });
      });
    }
    return teams;
  } catch (error) {
    console.error(`[CloudSync] Fetch my teams failed:`, error);
    return [];
  }
}

// Alias kept for older call sites (Ecosystem screen).
export const searchPlayerByUsername = searchPlayerInCloud;

/**
 * Remembers the XI that actually played, so the next import preselects it.
 * Allowed for any roster member (see firestore.rules).
 */
export async function updateTeamLastXI(teamId, names) {
  const user = get(currentUser);
  if (!user || !user.uid || !teamId) return false;
  try {
    const payload = {
      lastXI: (names || []).filter(Boolean).slice(0, 25),
      updatedAt: new Date().toISOString()
    };
    await setDoc(doc(db, 'teams', teamId), payload, { merge: true });
    return true;
  } catch (error) {
    console.warn('[CloudSync] lastXI update failed, queuing for later:', error);
    enqueueSyncAction('updateLastXI', {
      lastXI: (names || []).filter(Boolean).slice(0, 25),
      updatedAt: new Date().toISOString()
    }, { teamId });
    return false;
  }
}

// ─── GLOBAL PLAYER STATS (performances) ─────────────────────────────────

function _resolveLinkedPlayers() {
  // cs_player_registry maps display names → imported cloud profiles
  // (written by the Import Profile flow and by cloud-team roster imports).
  try {
    const reg = JSON.parse(localStorage.getItem('cs_player_registry') || '{}');
    const byName = {};
    for (const value of Object.values(reg)) {
      if (value?.uid && value?.displayName) byName[value.displayName] = value;
    }
    return byName;
  } catch (_) {
    return {};
  }
}

/**
 * After a match completes, writes one immutable performance doc per LINKED
 * player (players imported via @username). Each player's own phone later
 * aggregates these into their career stats — works even when someone else
 * scored the match. Deterministic ids + create-only rules make re-runs no-ops.
 */
export async function recordGlobalPerformances(match) {
  const user = get(currentUser);
  if (!user || !user.uid) return 0;
  if (!match?.id || !match?.result || !Array.isArray(match.innings)) return 0;

  const linked = _resolveLinkedPlayers();
  const perPlayer = {};

  match.innings.forEach(inn => {
    const battingTeamName = inn.battingTeam === 'A' ? match.teamA.name : match.teamB.name;
    const bowlingTeamName = inn.battingTeam === 'A' ? match.teamB.name : match.teamA.name;

    inn.batsmen.forEach(b => {
      const profile = linked[b.name];
      if (!profile || (b.balls || 0) + (b.runs || 0) === 0 && !b.out) return;
      const p = perPlayer[profile.uid] ||= {
        uid: profile.uid, name: b.name, teamName: battingTeamName,
        batting: { runs: 0, balls: 0, fours: 0, sixes: 0, outs: 0 },
        bowling: { balls: 0, runs: 0, wickets: 0, maidens: 0 }
      };
      p.batting.runs += b.runs || 0;
      p.batting.balls += b.balls || 0;
      p.batting.fours += b.fours || 0;
      p.batting.sixes += b.sixes || 0;
      if (b.out) p.batting.outs += 1;
    });

    inn.bowlers.forEach(bw => {
      const profile = linked[bw.name];
      if (!profile || (bw.balls || 0) === 0) return;
      const p = perPlayer[profile.uid] ||= {
        uid: profile.uid, name: bw.name, teamName: bowlingTeamName,
        batting: { runs: 0, balls: 0, fours: 0, sixes: 0, outs: 0 },
        bowling: { balls: 0, runs: 0, wickets: 0, maidens: 0 }
      };
      p.bowling.balls += bw.balls || 0;
      p.bowling.runs += bw.runs || 0;
      p.bowling.wickets += bw.wickets || 0;
      p.bowling.maidens += bw.maidens || 0;
    });
  });

  const winnerName = match.result.winner === 'A' ? match.teamA.name
    : match.result.winner === 'B' ? match.teamB.name : null;

  // OFFLINE-SAFE: fire ALL writes in PARALLEL, never sequential-await. With the
  // persistent cache, an offline setDoc() promise stays PENDING until the
  // network returns — a sequential `await` would block on the FIRST player and
  // every other player's local mutation would never even be queued (lost).
  // Firing them together stages all mutations in IndexedDB immediately; we then
  // only wait briefly for server acks so the caller never hangs.
  const players = Object.values(perPlayer);
  const writes = players.map(p => {
    const docId = `${match.id}_${p.uid}`;
    const payload = {
      uid: p.uid,
      matchId: match.id,
      scorerUid: user.uid,
      date: match.date || new Date().toISOString(),
      teamName: p.teamName,
      matchLabel: `${match.teamA.name} vs ${match.teamB.name}`,
      won: winnerName ? p.teamName === winnerName : null,
      mom: match.result?.mom === p.name,
      batting: p.batting,
      bowling: p.bowling,
      createdAt: new Date().toISOString()
    };
    return setDoc(doc(db, 'performances', docId), payload)
      .then(() => true)
      .catch(error => {
        // permission-denied here usually means the doc already exists
        // (create-only rules) — that is the intended dedup, not a failure.
        if (error?.code === 'permission-denied') return true;
        console.warn(`[CloudSync] Performance write failed for ${p.name}, queuing:`, error?.code || error);
        enqueueSyncAction('recordPerformance', payload, { docId });
        return false;
      });
  });

  if (players.length === 0) return 0;
  const OFFLINE_STAGED = Symbol('staged');
  const result = await Promise.race([
    Promise.all(writes).then(list => list.filter(Boolean).length),
    new Promise(resolve => setTimeout(() => resolve(OFFLINE_STAGED), 5000))
  ]);
  // Offline: all mutations are safely staged in the cache and will auto-sync.
  return result === OFFLINE_STAGED ? players.length : result;
}

/**
 * Fetches the signed-in user's own performance docs (for career stats).
 */
export async function fetchMyPerformances() {
  const user = get(currentUser);
  if (!user || !user.uid) return [];
  try {
    const snap = await getDocs(query(collection(db, 'performances'), where('uid', '==', user.uid), limit(50)));
    const out = [];
    snap.forEach(d => out.push(d.data()));
    return out;
  } catch (error) {
    console.error('[CloudSync] Fetch performances failed:', error);
    return [];
  }
}
